import { action, observable, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { EntityModules, MonitoringMethod, UserRole } from 'core/constants';
import { ProfileOrganisationResponse, SurveyResponse } from 'utils/api/types';
import { HelpCenterUrlsResponse, UserLicensesResponse, UserProfileResponse } from 'utils/api/modules/user/user.types';
import { Dashboards } from 'utils/api/modules/common/common.types';
import { EventType } from 'utils/events/constants';
import { ActionEvent, UpdatedProfilePayload, UpdatedUserPayload } from 'utils/events/types';

import { ADMIN_AND_MANAGERS } from '../../utils/roles/roleGroups';

export const hasFunctionalModuleLicense = (licenses: UserLicensesResponse): boolean =>
  licenses.events || licenses.riskAssessments || licenses.workflows || licenses.documents || licenses.risks;

// The products advertised on the More apps page — keep in sync with SolutionsPane.
const PRODUCT_LICENSES = ['protectorAlarms', 'workflows', 'events', 'documents'] as const;

export const hasAllProductLicenses = (licenses: UserLicensesResponse): boolean =>
  PRODUCT_LICENSES.every((license) => licenses[license]);

class SessionUser {
  uuid: string;

  @observable role: UserRole;

  email: string;

  @observable name: string;

  @observable phoneNumber: string | null;

  monitoringMethod: MonitoringMethod;

  organisation: ProfileOrganisationResponse;

  licenses: UserLicensesResponse;

  activeEntityModules: EntityModules[];

  activeOrganisationEntityModules: EntityModules[];

  @observable twoFactorAuthEnabled: boolean;

  @observable twoFactorAuthAllowed: boolean;

  @observable ssoUser: boolean;

  @observable showSurvey: boolean;

  @observable survey: SurveyResponse;

  @observable dashboards: Dashboards | null;

  readOnly: boolean;

  // Permission: can this user edit Object Manager fields (region, custom fields)?
  @observable showObjectManager: boolean;

  // Capability: does the org have Object Manager enabled at all (any active user carries it)?
  @observable hasObjectManager: boolean;

  @observable enableDashboardBuilder: boolean;

  @observable tiptapAiUserToken: string | null;

  @observable tiptapConvertToken: string | null;

  @observable scheduleEnabled: boolean;

  @observable auditsAsEntityEnabled: boolean;

  @observable trainingRecordsEnabled: boolean;

  @observable loneWorkingAnalyticsEnabled: boolean;

  @observable documentAcknowledgementsEnabled: boolean;

  @observable riskAssessmentEnabled: boolean;

  @observable risksAssessmentsModuleEnabled: boolean;

  @observable documentsSettingsTabEnabled: boolean;

  @observable repeatingGroupsEnabled: boolean;

  @observable staffRepeatingGroupEnabled: boolean;

  @observable groupsEnabled: boolean;

  @observable scopedPermissions: boolean;

  @observable conditionalStageFieldsEnabled: boolean;

  @observable timezoneModelEnabled: boolean;

  @observable securitasMastermindEnabled: boolean;

  helpCenterUrls: HelpCenterUrlsResponse;

  constructor(data: UserProfileResponse) {
    makeObservable(this);
    this.uuid = data.uuid;
    this.name = data.name;
    this.role = data.role;
    this.phoneNumber = data.phoneNumber;
    this.email = data.email;
    this.monitoringMethod = data.monitoringMethod;
    this.organisation = data.organisation;
    this.twoFactorAuthEnabled = data.twoFactorAuthEnabled;
    this.twoFactorAuthAllowed = data.twoFactorAuthAllowed;
    this.ssoUser = data.ssoUser;
    this.licenses = data.licenses;
    this.showSurvey = data.showSurvey;
    this.survey = data.survey;
    this.dashboards = data.dashboards;
    this.readOnly = data.readOnly;
    this.showObjectManager = data.showObjectManager;
    this.hasObjectManager = data.hasObjectManager;
    this.enableDashboardBuilder = data.enableDashboardBuilder;
    this.tiptapAiUserToken = data.tiptapAiUserToken;
    this.tiptapConvertToken = data.tiptapConvertToken;
    this.helpCenterUrls = data.helpCenterUrls;
    this.scheduleEnabled = data.scheduleEnabled;
    this.auditsAsEntityEnabled = data.auditsAsEntityEnabled;
    this.trainingRecordsEnabled = data.trainingRecordsEnabled;
    this.loneWorkingAnalyticsEnabled = data.loneWorkingAnalyticsEnabled;
    this.documentAcknowledgementsEnabled = data.documentAcknowledgementsEnabled;
    this.riskAssessmentEnabled = data.riskAssessmentEnabled;
    this.risksAssessmentsModuleEnabled = data.risksAssessmentsModuleEnabled;
    this.documentsSettingsTabEnabled = data.documentsSettingsTabEnabled;
    this.repeatingGroupsEnabled = data.repeatingGroupsEnabled;
    this.staffRepeatingGroupEnabled = data.staffRepeatingGroupEnabled;
    this.groupsEnabled = data.groupsEnabled;
    this.scopedPermissions = data.scopedPermissions;
    this.conditionalStageFieldsEnabled = data.conditionalStageFieldsEnabled;
    this.timezoneModelEnabled = data.timezoneModelEnabled;
    this.securitasMastermindEnabled = data.securitasMastermindEnabled;

    this.activeEntityModules = this.getAllAvailableEntitiesForUser();
    this.activeOrganisationEntityModules = this.getAllAvailableEntitiesByOrganisation();

    eventBus.register(this);
  }

  @action
  enableTwoFactorAuth(): void {
    this.twoFactorAuthEnabled = true;
  }

  @action
  disableTwoFactorAuth(): void {
    this.twoFactorAuthEnabled = false;
  }

  @action
  setTiptapTokens(tokens: { tiptapAiUserToken: string | null; tiptapConvertToken: string | null }): void {
    this.tiptapAiUserToken = tokens.tiptapAiUserToken;
    this.tiptapConvertToken = tokens.tiptapConvertToken;
  }

  @subscribe(EventType.UpdatedUser)
  @subscribe(EventType.UpdatedProfile)
  @action
  updatedUser({ payload }: ActionEvent<UpdatedProfilePayload | UpdatedUserPayload>): void {
    if (payload.uuid !== this.uuid) {
      return;
    }

    this.name = payload.name;
    this.phoneNumber = payload.phoneNumber;
    this.role = payload.role;
  }

  getAllAvailableEntitiesForUser(): EntityModules[] {
    const availableEntities: EntityModules[] = [];
    // events
    if (this.licenses.events) {
      availableEntities.push(EntityModules.Events);
    }
    // sites
    if (ADMIN_AND_MANAGERS.includes(this.role) && this.organisation.maximumSitesAllowed > 0) {
      availableEntities.push(EntityModules.Sites);
    }
    // assets
    if (ADMIN_AND_MANAGERS.includes(this.role) && this.organisation.maximumAssetsAllowed > 0) {
      availableEntities.push(EntityModules.Assets);
    }
    // contacts
    if (ADMIN_AND_MANAGERS.includes(this.role) && this.organisation.maximumContactsAllowed > 0) {
      availableEntities.push(EntityModules.Contacts);
    }
    // riskAssessments
    if (this.licenses.riskAssessments) {
      availableEntities.push(EntityModules.RisksAssessments);
    }
    // documents
    if (this.licenses.documents) {
      availableEntities.push(EntityModules.Documents);
    }
    // risks
    if (this.licenses.risks) {
      availableEntities.push(EntityModules.Risks);
    }
    // audits (all roles — User sees assigned audits, enforced server-side). The licence must be
    // checked alongside the flag: the backend gates the AUDITS entity type on WORKFLOWS, so a
    // flag-on org without it would otherwise get the nav and routes and then a 403 from the list.
    if (this.auditsAsEntityEnabled && this.licenses.workflows) {
      availableEntities.push(EntityModules.Audits);
    }

    return availableEntities;
  }

  getAllAvailableEntitiesByOrganisation(): EntityModules[] {
    const availableEntities: EntityModules[] = [];
    // sites
    if (this.organisation.maximumSitesAllowed > 0) {
      availableEntities.push(EntityModules.Sites);
    }
    // assets
    if (this.organisation.maximumAssetsAllowed > 0) {
      availableEntities.push(EntityModules.Assets);
    }
    // contacts
    if (this.organisation.maximumContactsAllowed > 0) {
      availableEntities.push(EntityModules.Contacts);
    }
    // riskAssessments
    if (this.licenses.riskAssessments) {
      availableEntities.push(EntityModules.RisksAssessments);
    }
    // events
    if (this.licenses.events) {
      availableEntities.push(EntityModules.Events);
    }
    // documents
    if (this.licenses.documents) {
      availableEntities.push(EntityModules.Documents);
    }
    // risks
    if (this.licenses.risks) {
      availableEntities.push(EntityModules.Risks);
    }
    // audits
    if (this.auditsAsEntityEnabled && this.licenses.workflows) {
      availableEntities.push(EntityModules.Audits);
    }
    return availableEntities;
  }

  hasOnlyLoneWorkingLicenseEnabled(): boolean {
    return this.licenses.protectorAlarms && !this.licenses.workflows;
  }

  hasFunctionalModuleLicense(): boolean {
    return hasFunctionalModuleLicense(this.licenses);
  }

  hasAllProductLicenses(): boolean {
    return hasAllProductLicenses(this.licenses);
  }
}

export default SessionUser;
